import React, { createContext, useReducer, useCallback } from "react";
import Swal from "sweetalert2";
import axios from "../../axios.js";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const reducer = (state, action) => {
  switch (action.type) {
    case "SET_LETTERS":
      return { ...state, letters: action.payload };
    
    case "SET_APPROVER_LETTERS":
      return { ...state, reviews: action.payload };

    case "SET_ONE_LETTER":
      return { ...state, letter: action.payload };

    case "SET_VIEW_LETTER":
      return { ...state, letter: action.payload };

    case "SET_MEMOS":
      return { ...state, memos: action.payload };

    case "SET_COMPANIES":
      return { ...state, companies: action.payload };
    
    case "SET_APPROVERS":
      return { ...state, approvers: action.payload };

    case "SET_EMPLOYERS":
      return { ...state, employers: action.payload };
    
    case "SET_ACTIVITIES":
      return { ...state, activites: action.payload };

    default:
      return state;
  }
};

const LetterContext = createContext({
  letters: [],
  reviews: [],
  letter: {},
  memos: [],
  companies: [],
  approvers: [],
  activities: [],
  employers: [],
  GetLetters: () => {},
  GetApproverLetters: () => {},
  GetOneLetter: () => {},
  GetCompanies: () => {},
  GetEmployers: () => {},
  GetApprovers: () => {},
  GetActivities: () => {},
  GetViewLetter: () => {},
  GetQRViewLetter: () => {},
  GetReportHtml: () => {},
  GetMemos: () => {},
  GetPdfDocument: () => {},
  AddLetter: () => {},
  UpdateLetter: () => {},
  ApprovedLetter: () => {},
  DeleteLetter: () => {},
  RemoveAttachment: () => {},
});

export const LetterProvider = ({ children }) => {
    const initialState = {
        letters: [],
        reviews: [],
        letter: {},
        memos: [],
        companies: [],
        employers: [],
        approvers: [],
        activities: []
    };

    const [state, dispatch] = useReducer(reducer, initialState);

    const GetLetters = useCallback(async ({ keyword = "", status = [], page = 1, limit = 10 } = {}) => {
        try {
            const params = new URLSearchParams();
            if (keyword) params.append("keyword", keyword);
            if (status.length > 0) status.forEach(s => params.append("status", s));
            params.append("page", page);
            params.append("limit", limit);

            const res = await axios.get(`${process.env.REACT_APP_TRANSACTIONS}letters?${params.toString()}`);
            if (res.data.success) {
                dispatch({ type: "SET_LETTERS", payload: res.data.payload });
                return res.data;
            }
            return { payload: [], pagination: { total_items: 0, total_pages: 0, current_page: 1 } };
        } catch (err) {
            return { payload: [], pagination: { total_items: 0, total_pages: 0, current_page: 1 } };
        }
    }, [dispatch]);

    const GetApproverLetters = useCallback(async ({ keyword = "", status = [], page = 1, limit = 10 } = {}) => {
        try {
            const params = new URLSearchParams();
            if (keyword) params.append("keyword", keyword);
            if (status.length > 0) status.forEach(s => params.append("status", s));
            params.append("page", page);
            params.append("limit", limit);

            const res = await axios.get(`${process.env.REACT_APP_TRANSACTIONS}letters/approvers?${params.toString()}`);
            if (res.data.success) {
                dispatch({ type: "SET_APPROVER_LETTERS", payload: res.data.payload });
                return res.data;
            }
            return { payload: [], pagination: { total_items: 0, total_pages: 0, current_page: 1 } };
        } catch (err) {
            return { payload: [], pagination: { total_items: 0, total_pages: 0, current_page: 1 } };
        }
    }, [dispatch]);

    const GetOneLetter = useCallback(async (code) => {
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_TRANSACTIONS}letters/${code}`
            );
            if (res.data.success) {
                dispatch({ type: "SET_ONE_LETTER", payload: res.data.payload });
                return res.data.payload;
            }
            return res.data;
        } catch (err) {
            return null;
        }
    }, []);

    const GetReportHtml = useCallback(async (cm_id) => {
        try {
            const res = await axios.post(
                `${process.env.REACT_APP_TRANSACTIONS}letters/report/html/${cm_id}`
            );
            if (res.data.success) {
                dispatch({ type: "SET_ONE_LETTER", payload: res.data.payload });
                return res.data;
            }
            return res.data;
        } catch (err) {
            return null;
        }
    }, []);

    const GetViewLetter = useCallback(async (formData) => {
        try {
            const res = await axios.post(`${process.env.REACT_APP_TRANSACTIONS}letters/item/reviews`, formData);
            if (res.data.success) {
                dispatch({ type: "SET_VIEW_LETTER", payload: res.data.payload });
                return res.data.payload;
            }
            return res.data;
        } catch (err) {
            return null;
        }
    }, []);

    const GetQRViewLetter = useCallback(async (data) => {
        try {
            const res = await axios.post(`${process.env.REACT_APP_TRANSACTIONS}letters/outside/surkom/view/${data}`);
            if (res.data.success) {
                dispatch({ type: "SET_VIEW_LETTER", payload: res.data.payload });
                return res.data.payload;
            }
            return res.data;
        } catch (err) {
            return null;
        }
    }, []);

    const GetCompanies = useCallback(async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_INFORMATIONS}companies`);
            if (res.data.success) {
                dispatch({ type: "SET_COMPANIES", payload: res.data.payload });
            }
        } catch (err) {
            console.error("GetCompanies Error:", err);
        }
    }, []);

    const GetApprovers = useCallback(async (code) => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_TRANSACTIONS}letters/participants/${code}`);
            if (res.data.success) {
                dispatch({ type: "SET_APPROVERS", payload: res.data.payload });
                return res.data;
            }
            return [];
        } catch (err) {
            return [];
        }
    }, []);

    const GetEmployers = useCallback(async (company_id) => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_TRANSACTIONS}letters/employers/users/${company_id}`);
            if (res.data.success) {
                dispatch({ type: "SET_EMPLOYERS", payload: res.data.payload });
                return res.data;
            }
            return [];
        } catch (err) {
            return [];
        }
    }, []);

    const GetActivities = useCallback(async (code) => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_TRANSACTIONS}letters/activities/${code.trim()}`);
            if (res.data.success) {
                dispatch({ type: "SET_ACTIVITIES", payload: res.data.payload });
                return res.data;
            }
            return [];
        } catch (err) {
            return [];
        }
    }, []);

    const GetMemos = useCallback(async (keyword = "") => {
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_TRANSACTIONS}letters/internal-memo?keyword=${keyword.trim()}`
            );
            if (res.data.success) {
                dispatch({ type: "SET_MEMOS", payload: res.data.payload });
                return res.data;
            }
            return [];
        } catch (err) {
            return [];
        }
    }, []);

    const GetPdfDocument = async (filename, cm_id = undefined) => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_TRANSACTIONS}letters/view/${cm_id}/document/${filename}`,{ responseType: "blob" });

            if (!res.data) throw new Error("Failed to load document");

            const ext = filename.split('.').pop().toLowerCase();
            let type = "unknown";

            if (["xlsx", "xls"].includes(ext)) type = "excel";
            else if (ext === "pdf") type = "pdf";
            else if (["png", "jpg", "jpeg", "gif", "bmp", "webp"].includes(ext)) type = "image";

            const blob = new Blob([res.data], { type: res.headers["content-type"] });
            return { filename, type, data: blob };
        } catch (err) {
            console.error("View document error:", err);
            throw err;
        }
    };

    const AddLetter = useCallback(async (formData) => {
        try {
            const res = await axios.post(`${process.env.REACT_APP_TRANSACTIONS}letters/submitted`, 
                formData, { headers: { "Content-Type": "multipart/form-data" } 
            });

            if (res.data.success) {
                toast.success("Letter added successfully!");
                GetLetters();
                return { success: true };
            }

            return { success: false };
        } catch (err) {
            toast.error(err.message || "Internal server error, please try again later !");
            return { success: false };
        }
    }, [GetLetters]);

    const UpdateLetter = useCallback(async (item) => {
        try {
            const res = await axios.patch(`${process.env.REACT_APP_TRANSACTIONS}letters/updated/${item.id}`, item.data,
                { headers: { "Content-Type": "multipart/form-data" } }
            );

            if (res.data.success) {
                return { success: true, message: res.data.message };
            }else{
                return { success: false, message: res.data.message };
            }
        } catch (err) {
            return { success: false, message: "Internal server error, please contact your administrator !" };
        }
    }, []);

    const ApprovedLetter = useCallback(async (formData) => {
        try {
            const res = await axios.post(
                `${process.env.REACT_APP_TRANSACTIONS}letters/approved`,
                formData
            );

            if (res.data.success) {
                return { success: true, message: res.data.message };
            } else {
                return { success: false , message: res.data.message };
            }
        } catch (err) {
            return { success: false, message: "Internal server error, please contact your administrator" };
        }
    }, []);

    const DeleteLetter = useCallback((code) => {
        return new Promise((resolve) => {
            Swal.fire({
            title: "Are you sure?",
            text: "This will permanently delete the letter!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, delete it!",
            }).then(async (result) => {
            if (!result.isConfirmed) {
                resolve({
                success: false,
                message: "The letter cancellation was aborted."
                });
                return;
            }

            try {
                const res = await axios.delete(
                `${process.env.REACT_APP_TRANSACTIONS}letters/removed/${code}`
                );

                resolve({
                success: res.data.success,
                message: res.data.message
                });
            } catch (err) {
                resolve({
                success: false,
                message: err.message
                });
            }
            });
        });
        }, []);

    const RemoveAttachment = useCallback(async ({ letter_id, filename }) => {
        try {
            const res = await axios.delete(`${process.env.REACT_APP_TRANSACTIONS}letters/attachment/removed/${letter_id}/document/${filename}`);

            if (res.data.success) {
                return { success: true, message: res.data.message };
            }
            return { success: false, message: res.data.message };
        } catch (err) {
            return { success: false, message: err.message.toString() };
        }
    }, []);

    return (
        <LetterContext.Provider
        value={{
            letters: state.letters,
            reviews: state.reviews,
            letter: state.letter,
            memos: state.memos,
            companies: state.companies,
            approvers: state.approvers,
            activities: state.activities,
            employers: state.employers,
            GetLetters,
            GetApproverLetters,
            GetOneLetter,
            GetCompanies,
            GetApprovers,
            GetActivities,
            GetViewLetter,
            GetQRViewLetter,
            GetReportHtml,
            GetMemos,
            GetEmployers,
            GetPdfDocument,
            AddLetter,
            UpdateLetter,
            ApprovedLetter,
            DeleteLetter,
            RemoveAttachment,
        }}
        >
        {children}
        <ToastContainer position="top-right" />
        </LetterContext.Provider>
    );
};

export default LetterContext;
